import {useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {Dialog} from "@headlessui/react";
import {Underwriting} from "api/quote";
import {Box} from "components/Box";
import Button from "components/Button";
import FormTitle from "pages/RateQuoteBind/components/FormTitle";
import {useSectionName} from "pages/RateQuoteBind/contexts/sectionNameContext";
import {useState} from "react";
import Field from "../../Field";

const AnchorLink = styled.a`
  margin: 4px;
`;
const DialogOverlay = styled(Dialog.Overlay)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.1);
`;

const DialogBody = styled.div`
  background-color: rgba(255, 255, 255, 1);
  width: 45rem;
  padding: 2.5rem;
  box-shadow: 0px 2px 4px ${(props) => props.theme.color.grayscale[300]};
  border-radius: 10px;
  z-index: 11;
`;

const DialogDescription = styled(Dialog.Description)`
  overflow: auto;
` as typeof Dialog.Description;
const DialogDescriptionWrapper = styled(Dialog.Description)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  height: 90%;
  -webkit-box-orient: vertical;
  overflow: auto;
` as typeof Dialog.Description;
const generalLiability = [
  {label: "$1,000,000 / $2,000,000", value: "2000000"},
  {label: "$500,000 / $1,000,000", value: "1000000"},
  {label: "$300,000 / $600,000", value: "300000"},
  {label: "Other", value: "other"},
];
export function DoesApplicantEngagInAnyFollowingOperationsActivitiesModal() {
  const [isOpen, setIsOpen] = useState(false);
  function closeModal() {
    setIsOpen(false);
  }

  function openModal(e: any) {
    e.preventDefault();
    setIsOpen(true);
  }
  return (
    <>
      Does The Applicant perform work related to any of
      <AnchorLink href="#" onClick={openModal}>
        these
      </AnchorLink>
      areas?
      <Dialog
        open={isOpen}
        as="div"
        onClose={closeModal}
        className="headlessDialog"
      >
        <DialogOverlay />
        <DialogBody>
          <DialogDescriptionWrapper>
            <Dialog.Title>
              Areas of work performed by the applicant
            </Dialog.Title>
            <DialogDescription>
              Worked performed by subcontractors who carry no general liability
              insurance, or carry general liability insurance with limits of
              liability less than the insureds limits of liability,or do not
              carry workers compensation insurance.
            </DialogDescription>
            <DialogDescription>
              Aerospace, airport work, railroads.
            </DialogDescription>
            <DialogDescription>
              Asbestos, mold, pollution or lead remediation.
            </DialogDescription>
            <DialogDescription>Amusement rides.</DialogDescription>
            <DialogDescription>
              Bridges, overpasses, tunnels, highways.
            </DialogDescription>
            <DialogDescription>
              Cell Towers / antennae more than 125 feet.
            </DialogDescription>
            <DialogDescription>
              Chemical, industrial, steam, or natural gas.
            </DialogDescription>
            <DialogDescription>
              Computer data center facilities.
            </DialogDescription>
            <DialogDescription>
              Dams, dikes, levees and reservoirs.
            </DialogDescription>
            <DialogDescription>
              Drilling, other than water wells.
            </DialogDescription>
            <DialogDescription>Earthquake reinforcement.</DialogDescription>
            <DialogDescription>
              Elevator installation or repair.
            </DialogDescription>
            <DialogDescription>
              Exterior Insulation Finishing Systems (EIFS).
            </DialogDescription>
            <DialogDescription>
              Fire suppression, proofing, escapes.
            </DialogDescription>
            <DialogDescription>
              Flood control or water damage restoration.
            </DialogDescription>
            <DialogDescription>
              Handling of fuel, oil, gas or petrochemicals.
            </DialogDescription>
            <DialogDescription>
              Hazardous substances or waste.
            </DialogDescription>
            <DialogDescription>Hospital or medical services.</DialogDescription>
            <DialogDescription>Hydronic or radiant heating.</DialogDescription>
            <DialogDescription>
              Mining, refining, oil field work.
            </DialogDescription>
            <DialogDescription>
              Nuclear facilities or operations.
            </DialogDescription>
            <DialogDescription>
              Pressure boilers and pressure vessels.
            </DialogDescription>
            <DialogDescription>
              Refrigeration systems in laboratories.
            </DialogDescription>
            <DialogDescription>
              Ship building, piers, wharves, seawalls.
            </DialogDescription>
            <DialogDescription>Street or road construction.</DialogDescription>
            <DialogDescription>
              Structural demolition or blasting.
            </DialogDescription>
            <DialogDescription>Tanks, water towers, silos.</DialogDescription>
            <DialogDescription>
              Underground pipelines or tanks.
            </DialogDescription>
            <DialogDescription>
              Water or sewage treatment plants.
            </DialogDescription>
          </DialogDescriptionWrapper>
          <Box width="100%" display="flex" justifyContent="center">
            <Button onClick={() => setIsOpen(false)}>Close</Button>
          </Box>
        </DialogBody>
      </Dialog>
    </>
  );
}
function UnderwritingForm({underwriting}: {underwriting?: Underwriting}) {
  const theme = useTheme();
  const sectionName = useSectionName();

  if (!underwriting) return <></>;

  return (
    <Box background={theme.color.brand.tertiary} py={34} px={32} mb={100}>
      <Box mb={32}>
        <FormTitle.Header2>{sectionName}</FormTitle.Header2>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="do_gross_annual_receipts_exceed"
            label="Do gross annual receipts exceed $1,500,00 including receipts for subcontracted work?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="does_the_maximum_gross_annual_payroll_exceed"
            label="Does the maximum gross annual payroll exceed $500,000?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="does_the_applicant_have_subcontractor_exceed_20p"
            label="Does the Applicant have subcontractor cost that exceed 20% of total receipts?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="is_this_a_new_venture"
            label="Is this a new Venture (6 months or less)?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="have_atleast_3_years_in_the_trade"
            label="Does the applicant have at least three years of experience in the trade being requested?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="applicant_required_license_in_jurisdictions"
            label="Are license(s) required in the jurisdictions where the applicant performs work?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        {underwriting.applicant_required_license_in_jurisdictions ? (
          <Box mb={16}>
            <Field.RadioGroup
              object={underwriting}
              field="applicant_have_required_license"
              label="Does the applicant have the required license(s)?"
              options={[
                {title: "Yes", value: 1},
                {title: "No", value: 0},
              ]}
            />
          </Box>
        ) : (
          <></>
        )}
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="perform_work_related_to_the_american_disabilities_act"
            label="Does the applicant perform work related to the American Disabilities Act involving installation or repair of chair lifts?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <DoesApplicantEngagInAnyFollowingOperationsActivitiesModal />
          <Field.RadioGroup
            object={underwriting}
            field="work_related_to_any_of_the_following"
            label=""
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="engage_in_exterior_work_3_stories"
            label="Does the business engage in exterior work involving more than 3 stories?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="perform_snow_ice_removal"
            label="Does Applicant perform snow and/or ice removal?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        {underwriting.perform_snow_ice_removal ? (
          <Box mb={16}>
            <Field.RadioGroup
              object={underwriting}
              field="separate_liability_policy_another_carrier"
              label="For applicants performing snow and/ or ice removal, does the applicant have a separate liability policy with another carrier?"
              options={[
                {title: "Yes", value: 1},
                {title: "No", value: 0},
              ]}
            />
          </Box>
        ) : (
          <></>
        )}
        {underwriting.separate_liability_policy_another_carrier ? (
          <Box mb={16}>
            <Field.TextInput
              object={underwriting}
              field="other_carrier_snow_name"
              label="Carrier Name"
            />
          </Box>
        ) : (
          <></>
        )}
        {underwriting.separate_liability_policy_another_carrier ? (
          <Box mb={16}>
            <Field.DropDown
              object={underwriting}
              field="other_carrier_snow_general_liability"
              label="General Liability"
              options={generalLiability.map((item) => ({
                title: item.label,
                value: item.value,
              }))}
            />
          </Box>
        ) : (
          <></>
        )}
        {underwriting.other_carrier_snow_general_liability === "other" ? (
          <Box mb={16}>
            <Field.TextArea
              object={underwriting}
              field="other_carrier_snow_details"
              label="Details"
            />
          </Box>
        ) : (
          <></>
        )}
        <Box mb={16}>
          <Field.RadioGroup
            object={underwriting}
            field="work_new_residential_construction"
            label="Does the Applicant perform any work in new residential construction?"
            options={[
              {title: "Yes", value: 1},
              {title: "No", value: 0},
            ]}
          />
        </Box>
        {underwriting.work_new_residential_construction ? (
          <Box mb={16}>
            <Field.RadioGroup
              object={underwriting}
              field="work_new_single_homes"
              label="Does Applicant perform any work on new single family homes, condominiums, cooperatives, or town house involving foundation work , framing, or roofing , without insured subcontractors with proper risk transfer in favor of Applicant?"
              options={[
                {title: "Yes", value: 1},
                {title: "No", value: 0},
              ]}
            />
          </Box>
        ) : (
          <></>
        )}
        {underwriting.work_new_residential_construction ? (
          <Box mb={16}>
            <Field.RadioGroup
              object={underwriting}
              field="work_new_developments_5"
              label="Does Applicant perform any work on new residentials developments of more than five (5) single family homes, condominiums, cooperatives, or townhouses in the same development involving plastering or stucco, siding or metal door and window installation?"
              options={[
                {title: "Yes", value: 1},
                {title: "No", value: 0},
              ]}
            />
          </Box>
        ) : (
          <></>
        )}
        {underwriting.work_new_residential_construction ? (
          <Box mb={16}>
            <Field.RadioGroup
              object={underwriting}
              field="work_new_developments_20"
              label="Does Applicant perform any work in new residential developments of more than twenty (20) single family homes, condominiums, cooperatives, or town houses in the same development? (Does not apply to new apartments or apartment buildings.)"
              options={[
                {title: "Yes", value: 1},
                {title: "No", value: 0},
              ]}
            />
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </Box>
  );
}

export default UnderwritingForm;
